/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react'
import { useTheme } from 'styled-components'
import ReactApexChart from 'react-apexcharts'
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaRocket,
  FaSearch,
  FaTrashAlt,
  FaCrosshairs,
  FaFileExcel,
} from 'react-icons/fa'
import { MdOutlineGridView, MdOutlineBarChart, MdOutlineScatterPlot } from 'react-icons/md'
import { SkuNavalBattleGrid } from './SkuNavalBattleGrid'
import {
  ContentCard,
  FilterBar,
  Matrix2x2Grid,
  MatrixItemCard,
  MatrixItemsList,
  MatrixQuadrantBox,
  MatrixQuadrantHeader,
  QuadrantLegendGrid,
  QuadrantLegendItem,
} from './styles'
import { CategoriaNaval, SubcategoriaNaval, MarcaNaval, FamiliaNaval } from './types'
import { exportRankingPorEstadoToExcel } from './exportExcel'

interface SkuNavalBattleChartProps {
  categorias?: CategoriaNaval[]
  subcategorias?: SubcategoriaNaval[]
  marcas?: MarcaNaval[]
  familias?: FamiliaNaval[]
  corteCat?: { mediaPdvs: number; mediaVendaSku: number }
  corteSub?: { mediaPdvs: number; mediaVendaSku: number }
  corteMarca?: { mediaPdvs: number; mediaVendaSku: number }
  corteFam?: { mediaPdvs: number; mediaVendaSku: number }
  distributorId?: string | number | null
  periodLabel?: string
  selectedDistributorName?: string
  startDate?: string
  endDate?: string
  uf?: string
  rankingUfs?: {
    marcas: Array<{ marca: string; uf: string; faturamento: number; caixas: number; pdvs?: number }>
    familias: Array<{ familia: string; uf: string; faturamento: number; caixas: number; pdvs?: number }>
  }
}

const formatMoney = (val: number) => {
  return (val || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  })
}

const formatNumber = (val: number) => {
  return Math.round(val || 0).toLocaleString('pt-BR')
}

const getQuadColor = (quad: 'Q1' | 'Q2' | 'Q3' | 'Q4') => {
  switch (quad) {
    case 'Q1': return '#10b981'
    case 'Q2': return '#3b82f6'
    case 'Q3': return '#f59e0b'
    case 'Q4': return '#ef4444'
  }
}

const renderTopSectionsHtml = (item: any, isLight: boolean) => {
  let html = ''

  if (item?.topUfs && item.topUfs.length > 0) {
    html += `
      <div style="margin-top: 10px; border-top: 1px dashed ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.15)'}; padding-top: 8px;">
        <div style="font-size: 11px; font-weight: 700; color: ${isLight ? '#475569' : '#cbd5e1'}; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Top 3 UFs</div>
        <div style="display: flex; flex-direction: column; gap: 4px;">
          ${item.topUfs.slice(0, 3).map((u: any, idx: number) => `
            <div style="display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: ${isLight ? '#334155' : '#cbd5e1'};">
              <span><strong style="color: ${isLight ? '#0f172a' : '#fff'};">${idx + 1}º ${u.nome}</strong>:</span>
              <span style="color: ${isLight ? '#475569' : '#94a3b8'};">${formatMoney(u.faturamento)} <strong style="color: ${isLight ? '#0f172a' : '#fff'};">(${u.percent}%)</strong></span>
            </div>
          `).join('')}
        </div>
      </div>
    `
  }

  if (item?.topDistribuidores && item.topDistribuidores.length > 0) {
    html += `
      <div style="margin-top: 10px; border-top: 1px dashed ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.15)'}; padding-top: 8px;">
        <div style="font-size: 11px; font-weight: 700; color: ${isLight ? '#475569' : '#cbd5e1'}; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Top 3 Distribuidores</div>
        <div style="display: flex; flex-direction: column; gap: 4px;">
          ${item.topDistribuidores.slice(0, 3).map((d: any, idx: number) => `
            <div style="display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: ${isLight ? '#334155' : '#cbd5e1'};">
              <span style="max-width: 170px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"><strong style="color: ${isLight ? '#0f172a' : '#fff'};">${idx + 1}º ${d.nome}</strong>:</span>
              <span style="color: ${isLight ? '#475569' : '#94a3b8'};">${formatMoney(d.faturamento)} <strong style="color: ${isLight ? '#0f172a' : '#fff'};">(${d.percent}%)</strong></span>
            </div>
          `).join('')}
        </div>
      </div>
    `
  }

  return html
}

export const SkuNavalBattleChart: React.FC<SkuNavalBattleChartProps> = ({
  categorias,
  subcategorias,
  marcas,
  familias,
  corteCat,
  corteSub,
  corteMarca,
  corteFam,
  distributorId,
  periodLabel,
  selectedDistributorName,
  startDate,
  endDate,
  uf,
  rankingUfs,
}) => {
  const theme = useTheme() as any
  const isLight = theme?.name === 'light'
  const [viewMode, setViewMode] = useState<'marca' | 'familia'>('marca')
  const [displayMode, setDisplayMode] = useState<'matriz' | 'ranking' | 'dispersao' | 'batalha'>('matriz')
  const [searchTerm, setSearchTerm] = useState('')

  const isMarca = viewMode === 'marca'
  const rawItems = isMarca ? (marcas || []) : (familias || [])
  const corteAtual = isMarca
    ? (corteMarca || corteCat || { mediaPdvs: 0, mediaVendaSku: 0 })
    : (corteFam || corteSub || { mediaPdvs: 0, mediaVendaSku: 0 })

  const handleExportRankingPorEstado = () => {
    exportRankingPorEstadoToExcel({
      viewMode,
      itemsGerais: rawItems.map((item: any) => ({
        displayName: isMarca ? (item.marca || 'Sem Marca') : (item.familia || 'Sem Família'),
        faturamento: Number(item.faturamento || 0),
        caixas: Number(item.caixas || 0),
      })),
      rankingUfs,
      periodLabel: periodLabel || 'Período Selecionado',
      selectedDistributorName: selectedDistributorName || 'Todos os Distribuidores (Nacional)',
      ufFiltro: uf || undefined,
    })
  }

  // Itens formatados e com busca
  const items = useMemo(() => {
    let list = rawItems.map((item: any) => ({
      ...item,
      displayName: isMarca
        ? (item.marca || 'Sem Marca')
        : (item.categoria ? `${item.familia} (${item.categoria})` : (item.familia || 'Sem Família')),
    }))

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim()
      list = list.filter((i: any) => i.displayName.toLowerCase().includes(term))
    }

    return list
  }, [rawItems, isMarca, searchTerm])

  // Agrupamento por Quadrante para a Matriz 2x2
  const quadItems = useMemo(() => {
    const q1 = items.filter(i => i.quadrante === 'Q1').sort((a, b) => b.vendaMediaPorSku - a.vendaMediaPorSku)
    const q2 = items.filter(i => i.quadrante === 'Q2').sort((a, b) => b.vendaMediaPorSku - a.vendaMediaPorSku)
    const q3 = items.filter(i => i.quadrante === 'Q3').sort((a, b) => b.vendaMediaPorSku - a.vendaMediaPorSku)
    const q4 = items.filter(i => i.quadrante === 'Q4').sort((a, b) => b.vendaMediaPorSku - a.vendaMediaPorSku)

    return { q1, q2, q3, q4 }
  }, [items])

  // Dados para o Gráfico de Barras Ranqueadas
  const barChartData = useMemo(() => {
    const sorted = [...items].sort((a, b) => b.vendaMediaPorSku - a.vendaMediaPorSku)
    const categories = sorted.map(i => i.displayName)
    const values = sorted.map(i => Math.round(i.vendaMediaPorSku))
    const colors = sorted.map(i => getQuadColor(i.quadrante))

    return { sorted, categories, values, colors }
  }, [items])

  const barChartOptions: any = useMemo(() => {
    return {
      chart: {
        type: 'bar',
        background: 'transparent',
        fontFamily: "'Montserrat', sans-serif",
        toolbar: { show: true },
      },
      theme: { mode: isLight ? 'light' : 'dark' },
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
          barHeight: '72%',
          borderRadius: 4,
          dataLabels: { position: 'top' },
        },
      },
      colors: barChartData.colors,
      grid: {
        borderColor: isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)',
        strokeDashArray: 4,
      },
      xaxis: {
        categories: barChartData.categories,
        labels: {
          style: { colors: isLight ? '#64748b' : '#94a3b8', fontSize: '11px' },
          formatter: (val: number) => {
            if (val >= 1000000) return `R$ ${(val / 1000000).toFixed(1)}M`
            if (val >= 1000) return `R$ ${(val / 1000).toFixed(0)}k`
            return `R$ ${val}`
          },
        },
      },
      yaxis: {
        labels: {
          style: { colors: isLight ? '#1e293b' : '#cbd5e1', fontSize: '11px', fontWeight: 600 },
          maxWidth: 240,
        },
      },
      legend: { show: false },
      dataLabels: {
        enabled: true,
        textAnchor: 'start',
        offsetX: 10,
        style: { colors: [isLight ? '#0f172a' : '#fff'], fontSize: '11px', fontWeight: 600 },
        formatter: (val: number) => formatMoney(val),
      },
      tooltip: {
        custom: ({ dataPointIndex }: any) => {
          const item = barChartData.sorted[dataPointIndex]
          if (!item) return ''
          const color = getQuadColor(item.quadrante)

          return `
            <div style="background: ${isLight ? '#ffffff' : '#0f172a'}; border: 1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.15)'}; border-radius: 10px; padding: 12px 16px; color: ${isLight ? '#0f172a' : '#fff'}; box-shadow: 0 10px 25px rgba(0,0,0,${isLight ? '0.1' : '0.5'}); font-family: 'Montserrat', sans-serif; min-width: 250px;">
              <div style="font-weight: 700; font-size: 14px; margin-bottom: 2px;">${item.displayName}</div>
              <div style="font-size: 11px; font-weight: 700; color: ${color}; margin-bottom: 8px;">Quadrante ${item.quadrante}</div>
              <div style="display: grid; grid-template-columns: auto auto; gap: 4px 14px; font-size: 12px; color: ${isLight ? '#64748b' : '#94a3b8'}; border-top: 1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}; padding-top: 6px;">
                <span>Venda Média / PDV:</span> <strong style="color:${isLight ? '#0f172a' : '#fff'}; text-align:right;">${formatMoney(item.vendaMediaPorSku)}</strong>
                <span>PDVs Positivados:</span> <strong style="color:${isLight ? '#0f172a' : '#fff'}; text-align:right;">${formatNumber(item.pdvsPositivados)} (${item.penetracaoPct}%)</strong>
                <span>SKUs Ativos:</span> <strong style="color:${isLight ? '#0f172a' : '#fff'}; text-align:right;">${item.skusAtivos} itens</strong>
                <span>Faturamento Total:</span> <strong style="color:${isLight ? '#0f172a' : '#fff'}; text-align:right;">${formatMoney(item.faturamento)}</strong>
                <span>Volume Caixas:</span> <strong style="color:${isLight ? '#0f172a' : '#fff'}; text-align:right;">${formatNumber(item.caixas)} cx</strong>
              </div>
              ${renderTopSectionsHtml(item, isLight)}
            </div>
          `
        },
      },
    }
  }, [barChartData, isLight])

  // Dados para o Gráfico de Dispersão (Scatter)
  const scatterSeries = useMemo(() => {
    const q1Data: any[] = []
    const q2Data: any[] = []
    const q3Data: any[] = []
    const q4Data: any[] = []

    items.forEach((item: any) => {
      const point = {
        x: item.pdvsPositivados,
        y: Math.round(item.vendaMediaPorSku),
        meta: {
          name: item.displayName,
          faturamento: item.faturamento,
          caixas: item.caixas,
          skus: item.skusAtivos,
          penetracao: item.penetracaoPct,
          mediaSkusPdv: item.mediaSkusPorPdv,
          quadrante: item.quadrante,
          topUfs: item.topUfs,
          topDistribuidores: item.topDistribuidores,
        },
      }

      switch (item.quadrante) {
        case 'Q1': q1Data.push(point); break
        case 'Q2': q2Data.push(point); break
        case 'Q3': q3Data.push(point); break
        case 'Q4': q4Data.push(point); break
      }
    })

    return [
      { name: 'Q1: Carros-Chefe (Alto Giro / Alta Cobertura)', data: q1Data },
      { name: 'Q2: Oportunidades (Alto Giro / Baixa Cobertura)', data: q2Data },
      { name: 'Q3: Presença (Baixo Giro / Alta Cobertura)', data: q3Data },
      { name: 'Q4: Cauda Longa (Baixo Giro / Baixa Cobertura)', data: q4Data },
    ]
  }, [items])

  const scatterChartOptions: any = useMemo(() => {
    return {
      chart: {
        type: 'scatter',
        background: 'transparent',
        fontFamily: "'Montserrat', sans-serif",
        toolbar: { show: true },
        animations: { enabled: true, speed: 400 },
      },
      colors: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'],
      theme: { mode: isLight ? 'light' : 'dark' },
      grid: {
        borderColor: isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)',
        strokeDashArray: 4,
      },
      markers: {
        size: isMarca ? 12 : 9,
        strokeWidth: 2,
        strokeColors: isLight ? '#ffffff' : '#fff',
        hover: { sizeOffset: 4 },
      },
      annotations: {
        xaxis: [
          {
            x: corteAtual.mediaPdvs,
            borderColor: isLight ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.4)',
            strokeDashArray: 6,
            label: {
              borderColor: isLight ? '#cbd5e1' : '#334155',
              style: { color: isLight ? '#0f172a' : '#fff', background: isLight ? '#f1f5f9' : '#1e293b', fontSize: '11px', fontWeight: 600 },
              text: `Média PDVs: ${formatNumber(corteAtual.mediaPdvs)}`,
            },
          },
        ],
        yaxis: [
          {
            y: corteAtual.mediaVendaSku,
            borderColor: isLight ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.4)',
            strokeDashArray: 6,
            label: {
              borderColor: isLight ? '#cbd5e1' : '#334155',
              style: { color: isLight ? '#0f172a' : '#fff', background: isLight ? '#f1f5f9' : '#1e293b', fontSize: '11px', fontWeight: 600 },
              text: `Média Giro: ${formatMoney(corteAtual.mediaVendaSku)}`,
            },
          },
        ],
      },
      xaxis: {
        title: {
          text: 'Cobertura de Mercado (PDVs Positivados)',
          style: { color: isLight ? '#334155' : '#cbd5e1', fontSize: '12px', fontWeight: 600 },
        },
        labels: {
          style: { colors: isLight ? '#64748b' : '#94a3b8', fontSize: '11px' },
          formatter: (val: number) => formatNumber(val),
        },
      },
      yaxis: {
        title: {
          text: 'Produtividade / Giro Médio por SKU (R$ / SKU)',
          style: { color: isLight ? '#334155' : '#cbd5e1', fontSize: '12px', fontWeight: 600 },
        },
        labels: {
          style: { colors: isLight ? '#64748b' : '#94a3b8', fontSize: '11px' },
          formatter: (val: number) => {
            if (val >= 1000000) return `R$ ${(val / 1000000).toFixed(1)}M`
            if (val >= 1000) return `R$ ${(val / 1000).toFixed(0)}k`
            return `R$ ${val}`
          },
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        labels: { colors: isLight ? '#334155' : '#cbd5e1' },
      },
      tooltip: {
        custom: ({ seriesIndex, dataPointIndex, w }: any) => {
          const point = w.config.series[seriesIndex]?.data[dataPointIndex]
          if (!point?.meta) return ''
          const m = point.meta
          const color = getQuadColor(m.quadrante)

          return `
            <div style="background: ${isLight ? '#ffffff' : '#0f172a'}; border: 1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.15)'}; border-radius: 10px; padding: 12px 16px; color: ${isLight ? '#0f172a' : '#fff'}; box-shadow: 0 10px 25px rgba(0,0,0,${isLight ? '0.1' : '0.5'}); font-family: 'Montserrat', sans-serif; min-width: 250px;">
              <div style="font-weight: 700; font-size: 14px; margin-bottom: 4px;">${m.name}</div>
              <div style="display: inline-block; font-size: 11px; font-weight: 700; color: ${color}; margin-bottom: 8px;">Quadrante ${m.quadrante}</div>
              <div style="display: grid; grid-template-columns: auto auto; gap: 4px 14px; font-size: 12px; color: ${isLight ? '#64748b' : '#94a3b8'}; border-top: 1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}; padding-top: 6px;">
                <span>Venda Média / PDV:</span> <strong style="color:${isLight ? '#0f172a' : '#fff'}; text-align:right;">${formatMoney(point.y)}</strong>
                <span>PDVs Positivados:</span> <strong style="color:${isLight ? '#0f172a' : '#fff'}; text-align:right;">${point.x.toLocaleString('pt-BR')} (${m.penetracao}%)</strong>
                <span>SKUs Ativos:</span> <strong style="color:${isLight ? '#0f172a' : '#fff'}; text-align:right;">${m.skus} itens</strong>
                <span>Faturamento Total:</span> <strong style="color:${isLight ? '#0f172a' : '#fff'}; text-align:right;">${formatMoney(m.faturamento)}</strong>
                <span>Volume Caixas:</span> <strong style="color:${isLight ? '#0f172a' : '#fff'}; text-align:right;">${formatNumber(m.caixas)} cx</strong>
              </div>
              ${renderTopSectionsHtml(m, isLight)}
            </div>
          `
        },
      },
    }
  }, [isMarca, corteAtual, isLight])

  return (
    <ContentCard>
      <FilterBar>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, color: isLight ? '#0f172a' : '#fff' }}>
            Batalha Naval: Matriz Estratégica de Sortimento & Giro
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: 12, color: isLight ? '#64748b' : '#94a3b8' }}>
            Linhas de corte: Cobertura média em <strong style={{ color: isLight ? '#0f172a' : '#fff' }}>{formatNumber(corteAtual.mediaPdvs)} PDVs</strong> &bull; Giro médio em <strong style={{ color: isLight ? '#0f172a' : '#fff' }}>{formatMoney(corteAtual.mediaVendaSku)}/SKU</strong>
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Busca */}
          {displayMode !== 'batalha' && (
            <div className='search-wrapper'>
              <FaSearch className='search-icon' />
              <input
                type='text'
                placeholder='Filtrar no gráfico/matriz...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ width: 190 }}
              />
            </div>
          )}

          {/* Alternância de Modo Visual */}
          <div className='view-toggle'>
            <button
              type='button'
              className={displayMode === 'matriz' ? 'active' : ''}
              onClick={() => setDisplayMode('matriz')}
              title='Matriz 2x2 com cards organizados sem sobreposição'
            >
              <MdOutlineGridView style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Matriz 2x2
            </button>
            <button
              type='button'
              className={displayMode === 'batalha' ? 'active' : ''}
              onClick={() => setDisplayMode('batalha')}
              title='Grade interativa de Batalha Naval com positivação'
            >
              <FaCrosshairs style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Batalha Naval
            </button>
            <button
              type='button'
              className={displayMode === 'ranking' ? 'active' : ''}
              onClick={() => setDisplayMode('ranking')}
              title='Ranking comparativo em barras ordenadas por giro'
            >
              <MdOutlineBarChart style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Ranking em Barras
            </button>
            <button
              type='button'
              className={displayMode === 'dispersao' ? 'active' : ''}
              onClick={() => setDisplayMode('dispersao')}
              title='Gráfico cartesiano de dispersão (Scatter)'
            >
              <MdOutlineScatterPlot style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Dispersão
            </button>
          </div>

          {/* Alternância Marca vs Família */}
          {displayMode !== 'batalha' && (
            <div className='view-toggle'>
              <button
                type='button'
                className={viewMode === 'marca' ? 'active' : ''}
                onClick={() => setViewMode('marca')}
              >
                Marca ({marcas?.length || 0})
              </button>
              <button
                type='button'
                className={viewMode === 'familia' ? 'active' : ''}
                onClick={() => setViewMode('familia')}
              >
                Família ({familias?.length || 0})
              </button>
            </div>
          )}
        </div>
      </FilterBar>

      {/* ========================================================================= */}
      {/* 1. MODO MATRIZ 2X2 DE CARDS (BCG BOARD SEM SOBREPOSIÇÃO) */}
      {/* ========================================================================= */}
      {displayMode === 'matriz' && (
        <Matrix2x2Grid>
          {/* QUADRANTE 2 (Topo Esquerdo: Oportunidades de Ouro) */}
          <MatrixQuadrantBox $quad='Q2'>
            <MatrixQuadrantHeader $quad='Q2'>
              <div className='info'>
                <h4>
                  <FaRocket color='#3b82f6' />
                  Q2: Oportunidades de Ouro
                </h4>
                <p>Alto giro unitário, porém baixa cobertura &bull; Ação: Distribuir em novos clientes</p>
              </div>
              <span className='quad-badge'>{quadItems.q2.length} itens</span>
            </MatrixQuadrantHeader>

            <MatrixItemsList>
              {quadItems.q2.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: 12, padding: 16, textAlign: 'center' }}>
                  Nenhum item neste quadrante
                </div>
              ) : (
                quadItems.q2.map(item => (
                  <MatrixItemCard key={item.displayName} $quadColor='#3b82f6'>
                    <div className='top-row'>
                      <span className='title'>{item.displayName}</span>
                      <span className='giro-val'>{formatMoney(item.vendaMediaPorSku)}/SKU</span>
                    </div>
                    <div className='meta-row'>
                      <span>{formatNumber(item.pdvsPositivados)} PDVs ({item.penetracaoPct}%)</span>
                      <span>{item.skusAtivos} SKUs &bull; {formatMoney(item.faturamento)}</span>
                    </div>
                    <div className='progress-bar'>
                      <div className='fill' style={{ width: `${Math.min(item.penetracaoPct, 100)}%` }} />
                    </div>
                  </MatrixItemCard>
                ))
              )}
            </MatrixItemsList>
          </MatrixQuadrantBox>

          {/* QUADRANTE 1 (Topo Direito: Carros-Chefe / Estrelas) */}
          <MatrixQuadrantBox $quad='Q1'>
            <MatrixQuadrantHeader $quad='Q1'>
              <div className='info'>
                <h4>
                  <FaCheckCircle color='#10b981' />
                  Q1: Carros-Chefe (Estrelas)
                </h4>
                <p>Alto giro unitário e alta cobertura &bull; Ação: Blindar gôndola e evitar ruptura</p>
              </div>
              <span className='quad-badge'>{quadItems.q1.length} itens</span>
            </MatrixQuadrantHeader>

            <MatrixItemsList>
              {quadItems.q1.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: 12, padding: 16, textAlign: 'center' }}>
                  Nenhum item neste quadrante
                </div>
              ) : (
                quadItems.q1.map(item => (
                  <MatrixItemCard key={item.displayName} $quadColor='#10b981'>
                    <div className='top-row'>
                      <span className='title'>{item.displayName}</span>
                      <span className='giro-val'>{formatMoney(item.vendaMediaPorSku)}/SKU</span>
                    </div>
                    <div className='meta-row'>
                      <span>{formatNumber(item.pdvsPositivados)} PDVs ({item.penetracaoPct}%)</span>
                      <span>{item.skusAtivos} SKUs &bull; {formatMoney(item.faturamento)}</span>
                    </div>
                    <div className='progress-bar'>
                      <div className='fill' style={{ width: `${Math.min(item.penetracaoPct, 100)}%` }} />
                    </div>
                  </MatrixItemCard>
                ))
              )}
            </MatrixItemsList>
          </MatrixQuadrantBox>

          {/* QUADRANTE 4 (Base Esquerda: Cauda Longa / Críticos) */}
          <MatrixQuadrantBox $quad='Q4'>
            <MatrixQuadrantHeader $quad='Q4'>
              <div className='info'>
                <h4>
                  <FaTrashAlt color='#ef4444' />
                  Q4: Cauda Longa / Críticos
                </h4>
                <p>Baixo giro unitário e baixa cobertura &bull; Ação: Avaliar substituição ou corte de mix</p>
              </div>
              <span className='quad-badge'>{quadItems.q4.length} itens</span>
            </MatrixQuadrantHeader>

            <MatrixItemsList>
              {quadItems.q4.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: 12, padding: 16, textAlign: 'center' }}>
                  Nenhum item neste quadrante
                </div>
              ) : (
                quadItems.q4.map(item => (
                  <MatrixItemCard key={item.displayName} $quadColor='#ef4444'>
                    <div className='top-row'>
                      <span className='title'>{item.displayName}</span>
                      <span className='giro-val'>{formatMoney(item.vendaMediaPorSku)}/SKU</span>
                    </div>
                    <div className='meta-row'>
                      <span>{formatNumber(item.pdvsPositivados)} PDVs ({item.penetracaoPct}%)</span>
                      <span>{item.skusAtivos} SKUs &bull; {formatMoney(item.faturamento)}</span>
                    </div>
                    <div className='progress-bar'>
                      <div className='fill' style={{ width: `${Math.min(item.penetracaoPct, 100)}%` }} />
                    </div>
                  </MatrixItemCard>
                ))
              )}
            </MatrixItemsList>
          </MatrixQuadrantBox>

          {/* QUADRANTE 3 (Base Direita: Presença & Volume) */}
          <MatrixQuadrantBox $quad='Q3'>
            <MatrixQuadrantHeader $quad='Q3'>
              <div className='info'>
                <h4>
                  <FaExclamationTriangle color='#f59e0b' />
                  Q3: Presença & Volume
                </h4>
                <p>Alta cobertura, porém baixo giro &bull; Ação: Combos, pontos extras e estímulo de giro</p>
              </div>
              <span className='quad-badge'>{quadItems.q3.length} itens</span>
            </MatrixQuadrantHeader>

            <MatrixItemsList>
              {quadItems.q3.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: 12, padding: 16, textAlign: 'center' }}>
                  Nenhum item neste quadrante
                </div>
              ) : (
                quadItems.q3.map(item => (
                  <MatrixItemCard key={item.displayName} $quadColor='#f59e0b'>
                    <div className='top-row'>
                      <span className='title'>{item.displayName}</span>
                      <span className='giro-val'>{formatMoney(item.vendaMediaPorSku)}/SKU</span>
                    </div>
                    <div className='meta-row'>
                      <span>{formatNumber(item.pdvsPositivados)} PDVs ({item.penetracaoPct}%)</span>
                      <span>{item.skusAtivos} SKUs &bull; {formatMoney(item.faturamento)}</span>
                    </div>
                    <div className='progress-bar'>
                      <div className='fill' style={{ width: `${Math.min(item.penetracaoPct, 100)}%` }} />
                    </div>
                  </MatrixItemCard>
                ))
              )}
            </MatrixItemsList>
          </MatrixQuadrantBox>
        </Matrix2x2Grid>
      )}

      {/* ========================================================================= */}
      {/* 2. MODO RANKING EM BARRAS HORIZONTAIS (ORDENADO E SEM SOBREPOSIÇÃO) */}
      {/* ========================================================================= */}
      {displayMode === 'ranking' && (
        <div style={{ width: '100%', minHeight: 480 }}>
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#10b981', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#10b981' }} /> Q1: Carro-Chefe
              </span>
              <span style={{ fontSize: 12, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#3b82f6' }} /> Q2: Oportunidade
              </span>
              <span style={{ fontSize: 12, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#f59e0b' }} /> Q3: Presença/Volume
              </span>
              <span style={{ fontSize: 12, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#ef4444' }} /> Q4: Cauda Longa
              </span>
            </div>

            <button
              type='button'
              onClick={handleExportRankingPorEstado}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                backgroundColor: '#10b981',
                color: '#ffffff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 'bold',
                cursor: 'pointer',
                fontFamily: "'Montserrat', sans-serif",
                boxShadow: '0 2px 6px rgba(16, 185, 129, 0.35)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.filter = 'brightness(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.filter = 'brightness(1.0)')}
              title='Exportar planilha Excel com ranking consolidado e abas por estado'
            >
              <FaFileExcel size={15} />
              Baixar Ranking por Estado
            </button>
          </div>

          <ReactApexChart
            options={barChartOptions}
            series={[{ name: 'Venda Média / PDV', data: barChartData.values }]}
            type='bar'
            height={Math.max(480, barChartData.categories.length * 32)}
            width='100%'
          />
        </div>
      )}

      {/* ========================================================================= */}
      {/* 3. MODO DISPERSÃO CARTESIANA (SCATTER CLÁSSICO) */}
      {/* ========================================================================= */}
      {displayMode === 'dispersao' && (
        <div style={{ width: '100%', minHeight: 460 }}>
          <ReactApexChart
            options={scatterChartOptions}
            series={scatterSeries}
            type='scatter'
            height={460}
            width='100%'
          />
        </div>
      )}

      {/* ========================================================================= */}
      {/* 4. MODO GRADE INTERATIVA DE BATALHA NAVAL */}
      {/* ========================================================================= */}
      {displayMode === 'batalha' && (
        <SkuNavalBattleGrid
          distributorId={distributorId}
          periodLabel={periodLabel || ''}
          selectedDistributorName={selectedDistributorName || ''}
          startDate={startDate}
          endDate={endDate}
          uf={uf}
        />
      )}

      {/* Legenda Resumida (apenas modos BCG) */}
      {displayMode !== 'batalha' && (
        <QuadrantLegendGrid>
          <QuadrantLegendItem $quad='Q1'>
            <div className='header'>
              <strong>Q1: Carros-Chefe (Estrelas)</strong>
              <span className='quad-pill'>ALTO GIRO | ALTA COBERTURA</span>
            </div>
            <p className='desc'>Categorias dominantes com grande faturamento por item e alta presença em clientes.</p>
          </QuadrantLegendItem>

          <QuadrantLegendItem $quad='Q2'>
            <div className='header'>
              <strong>Q2: Oportunidades de Ouro</strong>
              <span className='quad-pill'>ALTO GIRO | BAIXA COBERTURA</span>
            </div>
            <p className='desc'>Onde entra, vende muito, mas ainda está em poucos clientes. Ação: Expansão.</p>
          </QuadrantLegendItem>

          <QuadrantLegendItem $quad='Q3'>
            <div className='header'>
              <strong>Q3: Presença Consolidada</strong>
              <span className='quad-pill'>BAIXO GIRO | ALTA COBERTURA</span>
            </div>
            <p className='desc'>Presente em ampla rede de clientes, mas com menor faturamento por item.</p>
          </QuadrantLegendItem>

          <QuadrantLegendItem $quad='Q4'>
            <div className='header'>
              <strong>Q4: Cauda Longa / Críticos</strong>
              <span className='quad-pill'>BAIXO GIRO | BAIXA COBERTURA</span>
            </div>
            <p className='desc'>Pouca presença e baixo giro. Ação: Avaliar corte ou substituição de mix.</p>
          </QuadrantLegendItem>
        </QuadrantLegendGrid>
      )}
    </ContentCard>
  )
}
